/**
 * @module services/Modulo/Modulo
 * @category Serviços
 * @summary Módulo do serviço de resolução de módulos.
 *
 * @description
 * Expõe o objeto/_namespace_ do serviço de resolução de módulos do sistema para
 * utilização interna.
 *
 * O serviço de resolução de módulos permite que um módulo seja importado
 * dinamicamente, i.e., à medida em que o usuário o acesse. Internamente, o
 * serviço verifica se um módulo já foi carregado anteriormente, reaproveitando
 * os arquivos previamente baixados.
 *
 * Para correto funcionamento, é necessário que os gerenciadores de estado e
 * de rotas sejam registrados a este serviço.
 *
 * @requires module:constants.MODULOS_LIST_URL
 * @requires module:constants.MODULOS_ROUTE
 *
 * @example
 * import ModuloService from "./services/Modulo/Modulo";
 *
 * // Registrando os gerenciadores de estado e de rotas do container.
 * ModuloService.registerStore(store).registerRouter(router);
 *
 * const moduloMetadata = {
 *  systemName: "implanta",
 *  // ...
 * };
 *
 * // Importando o módulo Implanta
 * ModuloService.downloadModulo(moduloMetadata.systemName)
 *  .then(() => ModuloService.registerModulo(moduloMetadata, Modulo))
 *  .catch(
 *    // ...
 *  );
 */
import { MODULOS_LIST_URL, MODULOS_ROUTE } from "../../constants";
import { createScriptElement, createStyleElement } from "../../utils";

/**
 * @namespace ModuloService
 * @category Serviços
 * @summary Objeto/_namespace_ do serviço de resolução de módulos do sistema.
 */
const ModuloService = {
  /**
   * Gerenciador de rotas do sistema.
   * @type {external:VueRouter}
   * @private
   * @readonly
   */
  router: null,

  /**
   * Gerenciador do estado global do sistema.
   * @type {external:Store}
   * @private
   * @readonly
   */
  store: null,

  /**
   * Extrai o nome mnemônico do módulo a partir de uma rota.
   * @param {external:Route} route - Objeto representando a rota ativa.
   * @returns {string} Nome mnemônico do módulo.
   */
  getModuloSystemNameFromRoute(route) {
    if (!route || !route.path) {
      return;
    }

    const preffix = `${MODULOS_ROUTE}/`;

    if (!route.path.startsWith(preffix)) {
      return;
    }

    return route.path.substring(preffix.length).split("/")[0];
  },

  /**
   * Lista os metadados dos módulos do sistema.
   * @returns {Array<object>} Metadados dos módulos do sistema.
   */
  list() {
    return require("../../assets/modulos");
  },

  /**
   * Lista os metadados dos módulos do sistema, retornando apenas os módulos
   * que estão habilitados.
   * @returns {Array<object>} Metadados dos módulos habilitados do sistema.
   */
  listEnabled() {
    const env = process.env.VUE_APP_ENV;
    const modulos = require("../../assets/modulos");

    return modulos.filter(item => item.disabled[env] === false);
  },

  /**
   * Mapeia os metadados de um módulo do sistema em objeto reconhecido pelo
   * componente {@link module:components/base/BaseCard/BaseCard}.
   * @param {object} moduloMetadata - Metadados de um módulo do sistema.
   * @param {string} defaultActionName - Texto padrão para o link de acesso ao
   * módulo.
   * @param {string} defaultActionIcon - Nome do ícone padrão do Materialize a
   * ser utilizado.
   * @returns {object} Objeto reconhecido pelo componente {@link module:components/base/BaseCard/BaseCard}.
   */
  mapToCard(moduloMetadata, defaultActionName, defaultActionIcon) {
    const image = moduloMetadata.logo
      ? {
          src: moduloMetadata.logo,
          alt: `Logomarca do módulo ${moduloMetadata.name}`
        }
      : null;

    return {
      cardTitle: moduloMetadata.name,
      title: moduloMetadata.name,
      image,
      content: moduloMetadata.description,
      actions: [
        {
          to: `${MODULOS_ROUTE}/${moduloMetadata.systemName}`,
          content: defaultActionName ? defaultActionName : "Acessar",
          iconName: defaultActionIcon ? defaultActionIcon : "launch"
        }
      ]
    };
  },

  /**
   * Mapeia os metadados de um módulo do sistema em objeto reconhecido por
   * links do componente de sidenav.
   * @param {object} moduloMetadata - Metadados de um módulo do sistema.
   * @returns {object}
   */
  mapToSidenavLink(moduloMetadata) {
    return {
      content: moduloMetadata.name,
      to: `${MODULOS_ROUTE}/${moduloMetadata.systemName}`
    };
  },

  /**
   * Baixa um módulo do sistema, obtendo os metadados do módulo e criando
   * elementos HTML `script` e `link` para download dos código e estilo,
   * respectivamente, do módulo desejado.
   * @param {string} moduloSystemName - Nome mnemônico do módulo do sistema.
   * @returns {external:Promise} Promise indicando o sucesso ou falha nesta operação.
   */
  downloadModulo(moduloSystemName) {
    const moduloMetadata = this.store.getters.getModuloMetadata(
      "systemName",
      moduloSystemName
    );

    if (!moduloMetadata) {
      return Promise.reject(new Error("Módulo não encontrado"));
    }

    const scriptId = `script-modulo-${moduloSystemName}`;

    return new Promise(function(resolve, reject) {
      const scriptProps = {
        id: scriptId,
        src: `${MODULOS_LIST_URL}/${moduloSystemName}/${moduloSystemName}.umd.min.js`,
        onload: () => {
          if (moduloMetadata.hasStylesheet) {
            const cssProps = {
              href: `${MODULOS_LIST_URL}/${moduloSystemName}/${moduloSystemName}.css`,
              rel: "stylesheet"
            };

            createStyleElement(cssProps);
          }

          const downloadedModulo = window[moduloSystemName]
            ? window[moduloSystemName].default
            : null;

          delete window[moduloSystemName];

          if (!downloadedModulo) {
            return Promise.reject(
              new Error(
                `O módulo "${moduloMetadata.name}" não foi obtido corretamente do servidor`
              )
            );
          }

          if (!downloadedModulo.component) {
            return Promise.reject(
              new Error(
                `Não foi encontrado o componente do módulo "${moduloMetadata.name}"`
              )
            );
          }

          resolve(downloadedModulo);
        },
        onerror: () => {
          // Em casos de falha, remove-se o elemento HTML `script`
          // para que seja possível tentar-se novamente
          const oldScript = document.getElementById(scriptId);

          if (oldScript) {
            document.head.removeChild(oldScript);
          }

          reject(
            new Error(
              `Não foi possível obter do servidor o módulo "${moduloMetadata.name}"`
            )
          );
        }
      };

      createScriptElement(scriptProps);
    });
  },

  /**
   * Registra um módulo do sistema ao estado global e adiciona suas rotas
   * às rotas globais (utilizadas pelo container).
   * @param {string} moduloSystemName - Nome mnemônico do módulo do sistema.
   * @param {object} downloadedModulo - Código de um módulo do sistema, que já fora baixado.
   * @param {object} wrapperComponent - Componente do {@link external:Vue Vue} ao qual a rota do
   * módulo ficará associada.
   * @returns {module:services/Modulo/Modulo~ModuloService} O próprio namespace.
   */
  registerModulo(moduloSystemName, downloadedModulo, wrapperComponent) {
    const moduloMetadata = this.store.getters.getModuloMetadata(
      "systemName",
      moduloSystemName
    );

    if (!moduloMetadata) {
      throw new Error("Módulo não encontrado");
    }

    this.store.commit("addDownloadedModulo", {
      ...moduloMetadata,
      component: downloadedModulo.component,
      routes: downloadedModulo.routes,
      state: downloadedModulo.state
    });

    const moduloRouteConfig = {
      path: `${MODULOS_ROUTE}/${moduloSystemName}`,
      // TODO: definir propriedade name
      component: wrapperComponent,
      children: downloadedModulo.routes,
      props: () => ({ moduloSystemName }),
      meta: { title: moduloMetadata.name }
    };

    this.router.addRoutes([moduloRouteConfig]);

    return this;
  },

  /**
   * Ativa um módulo do sistema, adicionando seu estado local ao estado
   * global.
   * @param {string} moduloSystemName - Nome mnemônico do módulo do sistema.
   * @returns {modulo:services/Modulo/Modulo~ModuloService} O próprio namespace.
   */
  activateModulo(moduloSystemName) {
    const downloadedModulo = this.store.getters.getDownloadedModulo(
      "systemName",
      moduloSystemName
    );

    if (!downloadedModulo) {
      throw new Error(`O módulo ainda não foi obtido do servidor`);
    }

    this.store.registerModule(moduloSystemName, downloadedModulo.state);

    this.store.commit("setModulo", downloadedModulo);

    return this;
  },

  /**
   * Desativa um módulo, reiniciando algumas propriedades e remove seu estado
   * local do estado global do sistema.
   * @param {string} moduloSystemName - Nome mnemônico do módulo do sistema.
   * @returns {module:services/Modulo/Modulo~ModuloService} O próprio namespace.
   */
  deactivateModulo(moduloSystemName) {
    this.store.commit("setModulo", null);
    this.store.commit("setModuloMenu", []);
    this.store.unregisterModule(moduloSystemName);

    return this;
  },

  /**
   * Registra o gerenciador de rotas do sistema no _namespace_ do serviço.
   * @param {external:VueRouter} router - Gerenciador de rotas do sistema.
   * @returns {module:services/Modulo/Modulo~ModuloService} O próprio namespace.
   */
  registerRouter(router) {
    this.router = router;
    return this;
  },

  /**
   * Registra o gerenciador do estado global do sistema no _namespace_ do serviço.
   * @param {external:Store} store - Gerenciador do estado global do sistema.
   * @returns {module:services/Modulo/Modulo~ModuloService} O próprio namespace.
   */
  registerStore(store) {
    this.store = store;
    return this;
  }
};

export default ModuloService;
